import React from "react";
import { DEFAULT_MODEL_ID, LEGACY_2025_MODEL_IDS } from "../constants";
import { getModelIdForYear } from "qpr-base/context/modelsByYear";
import {
  extractElementModelId,
  extractPageFromPathParts,
  extractYearFromPeriodParam,
  isTenDigitModelId,
  splitPathParts,
} from "../urlUtils";

export const useRoutingGuards = (args: {
  tenantSymbol: string;
  ctx: any;
  params: { model?: string; id?: string; meaId?: string };
  location: { pathname: string; search: string };
  history: { replace: (path: string) => void };
}) => {
  const { tenantSymbol, ctx, params, location, history } = args;

  React.useEffect(() => {
    const pathParts = splitPathParts(location.pathname);
    const periodParam = new URLSearchParams(location.search).get("period");
    const periodYear = extractYearFromPeriodParam(periodParam);

    // Case 0: Fix malformed URLs with two model IDs (e.g., /tdf/{modelA}/{modelB})
    if (pathParts.length >= 3 && pathParts[0] === tenantSymbol) {
      const firstPart = pathParts[1];
      const secondPart = pathParts[2];
      const isFirstModelId = isTenDigitModelId(firstPart);
      const isSecondModelId = isTenDigitModelId(secondPart);

      if (isFirstModelId && isSecondModelId) {
        let correctModel = firstPart;
        if (periodYear) {
          correctModel = getModelIdForYear(periodYear) || firstPart;
        }
        const remainingParts = pathParts.slice(3);
        const page = remainingParts.length > 0 ? remainingParts[0] : ctx.page || "workflowadmin";
        const meaId = remainingParts.length > 1 ? remainingParts[1] : "";
        const redirectUrl = `/${tenantSymbol}/${correctModel}/${page}${
          meaId ? `/${meaId}` : ""
        }${location.search}`;
        history.replace(redirectUrl);
        return;
      }
    }

    // Case 1: URL like /{tenant}/{page} (no model)
    if (pathParts.length === 2 && pathParts[0] === tenantSymbol && !params.model) {
      const page = pathParts[1];
      if (page && page !== tenantSymbol) {
        const queryString = periodParam ? `?period=${periodParam}` : "";
        history.replace(`/${tenantSymbol}/${DEFAULT_MODEL_ID}/${page}${queryString}`);
        return;
      }
    }

    // Case 2: URL uses a 2025 model id but period is not explicitly 2025 → redirect to 2026 model
    if (params.model && LEGACY_2025_MODEL_IDS.has(params.model)) {
      const elementModelId = extractElementModelId(params.meaId);
      const elementModelMismatch = !!(elementModelId && elementModelId !== params.model);

      // If user is on the legacy 2025 model but period is explicitly 2025, redirect to canonical 2025 model id
      if (!elementModelMismatch && periodYear === "2025" && params.model === "827938453") {
        const page = extractPageFromPathParts({
          pathParts,
          tenantSymbol,
          modelId: params.model,
          fallback: params.id || "landingpage",
        });
        const meaId = params.meaId || "";
        const queryString = periodParam ? `?period=${periodParam}` : "";
        history.replace(
          `/${tenantSymbol}/${getModelIdForYear("2025") || "1954172446"}/${page}${meaId ? `/${meaId}` : ""}${queryString}`
        );
        return;
      }

      // If elementId contains a different model, redirect to match element model
      if (elementModelMismatch && elementModelId) {
        const page = extractPageFromPathParts({
          pathParts,
          tenantSymbol,
          modelId: params.model,
          fallback: params.id || "landingpage",
        });
        const meaId = params.meaId || "";
        const queryString = periodParam ? `?period=${periodParam}` : "";
        history.replace(
          `/${tenantSymbol}/${elementModelId}/${page}${meaId ? `/${meaId}` : ""}${queryString}`
        );
        return;
      }

      if (!periodYear || periodYear !== "2025") {
        const page = extractPageFromPathParts({
          pathParts,
          tenantSymbol,
          modelId: params.model,
          fallback: params.id || "landingpage",
        });
        const meaId = params.meaId || "";
        const queryString = periodParam && periodYear !== "2026" ? `?period=${periodParam}` : "";
        history.replace(
          `/${tenantSymbol}/${DEFAULT_MODEL_ID}/${page}${meaId ? `/${meaId}` : ""}${queryString}`
        );
        return;
      }
    }

    // Case 3: For any model, if elementId model doesn't match URL model, remove element id
    const urlModelId =
      params.model ||
      (pathParts.length >= 2 && isTenDigitModelId(pathParts[1]) ? pathParts[1] : null) ||
      ctx.modelId;

    if (params.meaId && urlModelId) {
      const elementModelId = extractElementModelId(params.meaId);
      if (elementModelId && elementModelId !== urlModelId) {
        const page = extractPageFromPathParts({
          pathParts,
          tenantSymbol,
          modelId: urlModelId,
          fallback: params.id || ctx.page || "landingpage",
        });
        ctx.sendValue("elementId", "");
        const queryString = periodParam ? `?period=${encodeURIComponent(periodParam)}` : "";
        history.replace(`/${tenantSymbol}/${urlModelId}/${page}${queryString}`);
      }
    }
  }, [ctx, history, location.pathname, location.search, params.id, params.meaId, params.model, tenantSymbol]);
};

